import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

const moneyStringToNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const match = String(value || '').replace(/,/g, '').match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : 0;
};

const centsToAmount = (cents) => {
  const numericCents = Number(cents);
  return Number.isFinite(numericCents) ? numericCents / 100 : 0;
};

const normalizeCartItem = (item) => {
  const quantity = Number(item.quantity) || 0;
  const unitPrice = item.unit_price_cents != null
    ? centsToAmount(item.unit_price_cents)
    : moneyStringToNumber(item.unit_price);
  const lineSubtotal = item.subtotal_cents != null
    ? centsToAmount(item.subtotal_cents)
    : unitPrice * quantity;

  return {
    id: item.id,
    productId: item.product_id || item.product?.id,
    variantId: item.variant_id || item.variant?.id || null,
    designId: item.design_id || item.design?.id || null,
    quantity,
    unitPrice,
    unitPriceCents: item.unit_price_cents,
    unitPriceDisplay: item.unit_price,
    subtotal: lineSubtotal,
    subtotalCents: item.subtotal_cents,
    subtotalDisplay: item.subtotal,
    customizations: item.customizations || {},
    product: item.product,
    variant: item.variant,
    design: item.design,
  };
};

const normalizeCartResponse = (data = {}) => {
  const rawItems = data.items || data.cart?.items || [];
  const items = rawItems.map(normalizeCartItem);
  const quote = data.quote || data.cart?.quote || {};
  const itemSubtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
  const subtotal = quote.subtotal != null ? moneyStringToNumber(quote.subtotal) : itemSubtotal;
  const discount = moneyStringToNumber(quote.discount);
  const tax = quote.tax != null ? moneyStringToNumber(quote.tax) : subtotal * 0.15;
  const shipping = moneyStringToNumber(quote.shipping);
  const total = quote.total != null
    ? moneyStringToNumber(quote.total)
    : subtotal - discount + tax + shipping;

  return {
    id: data.id || data.cart?.id || null,
    items,
    subtotal,
    discount,
    tax,
    shipping,
    total,
  };
};

export const fetchCart = createAsyncThunk(
  'cart/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/cart');
      return normalizeCartResponse(data);
    } catch (error) {
      if (error.response?.status === 401) {
        return { items: [], subtotal: 0, total: 0 };
      }
      return rejectWithValue(error.message || 'Failed to fetch cart');
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addItem',
  async ({ productId, variantId, quantity, customizations, designId }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.put('/cart', {
        product_id: productId,
        variant_id: variantId,
        quantity,
        customizations,
        design_id: designId,
      });

      // Refetch cart to get updated items
      dispatch(fetchCart());
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add item to cart');
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateItem',
  async ({ itemId, updates }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/cart/items/${itemId}/quantity`, {
        quantity: updates.quantity,
      });

      dispatch(fetchCart());
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update cart item');
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeItem',
  async (itemId, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/cart/items/${itemId}`);
      dispatch(fetchCart());
      return itemId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to remove item');
    }
  }
);

export const clearCartItems = createAsyncThunk(
  'cart/clear',
  async (_, { rejectWithValue }) => {
    try {
      await api.delete('/cart');
      return null;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to clear cart');
    }
  }
);

const initialState = {
  id: null,
  items: [],
  subtotal: 0,
  discount: 0,
  tax: 0,
  shipping: 0,
  total: 0,
  loading: false,
  error: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCart: (state) => {
      state.id = null;
      state.items = [];
      state.subtotal = 0;
      state.discount = 0;
      state.tax = 0;
      state.shipping = 0;
      state.total = 0;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.id = action.payload.id || null;
        state.items = action.payload.items || [];
        state.subtotal = action.payload.subtotal || 0;
        state.discount = action.payload.discount || 0;
        state.tax = action.payload.tax || 0;
        state.shipping = action.payload.shipping || 0;
        state.total = action.payload.total || 0;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Cart Item
      .addCase(updateCartItem.fulfilled, (state, action) => {
        const updatedItem = action.payload.cart_item
          ? normalizeCartItem(action.payload.cart_item)
          : action.payload;
        const index = state.items.findIndex(item => item.id === updatedItem.id);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...updatedItem };
          state.subtotal = state.items.reduce((sum, item) => sum + (item.subtotal || item.unitPrice * item.quantity), 0);
          state.total = state.subtotal;
        }
      })
      // Remove from Cart
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        state.subtotal = state.items.reduce((sum, item) => sum + (item.subtotal || item.unitPrice * item.quantity), 0);
        state.total = state.subtotal;
      })
      // Clear Cart
      .addCase(clearCartItems.fulfilled, (state) => {
        state.items = [];
        state.subtotal = 0;
        state.discount = 0;
        state.tax = 0;
        state.shipping = 0;
        state.total = 0;
      });
  },
});

export const { clearCart, clearError } = cartSlice.actions;
export default cartSlice.reducer;
